import { FileSourceEnum } from '@systemeio/file-manager'
import { FormInput, Hint, Tooltip } from '@systemeio/ui'
import { getServerSideFlagsProps } from 'growthbook/server'
import { FlagsPropsType, GrowthbookFlagsEnum } from 'growthbook/types'
import {
  activateCourseBundle,
  createCourseBundle,
  removeCourseBundle,
  updateCourseBundle,
} from 'modules/courses/bundles/api/course-bundles-api'
import { CourseBundlesLoco } from 'modules/courses/bundles/constants/course-bundles-loco'
import { useCourseBundles } from 'modules/courses/bundles/hooks/use-course-bundles'
import {
  CourseBundleCreateInterface,
  CourseBundleErrorsInterface,
  CourseBundleItemInterface,
} from 'modules/courses/bundles/types/course-bundle-interface'
import TabMenu from 'modules/courses/components/tab-menu'
import { NextPage } from 'next'
import { useState } from 'react'
import Confirmation from 'shared/components/confirmation-modal'
import { CreateDataType } from 'shared/components/create-modal'
import { ImageUploaderWithPreviewImage } from 'shared/components/file-uploader/image-uploader-with-preview-image'
import LinkWithoutPrefetch from 'shared/components/link-without-prefetch'
import ListingLayout from 'shared/components/listing-layout'
import { Table } from 'shared/components/table/table'
import { ColumnDefinitionInterface } from 'shared/components/table/types/column-interface'
import { RequestModuleEnum } from 'shared/enums/request-module-enum'
import { useLocoTranslation } from 'shared/hooks/use-loco-translation'
import { useRevalidateModules } from 'shared/hooks/use-revalidate-modules'
import { usePagination } from 'shared/route-query/hooks/use-pagination'
import { fetchWithErrors } from 'shared/utils/fetch-with-errors'
import {
  getBaseServerSideProps,
  getCombinedServerSideProps,
} from 'shared/utils/get-base-server-side-props'
import { CheckCircleIcon } from '@systemeio/ui/icons'

const defaultValues: CourseBundleCreateInterface = {
  name: '',
  description: '',
  instructorName: '',
  image: null,
}

const defaultErrors: CourseBundleErrorsInterface = {
  name: '',
  description: '',
  instructorName: '',
  image: '',
}

const CourseBundles: NextPage<FlagsPropsType> = ({ flags }) => {
  const { setPagination, pagination, setLimit, limit } = usePagination()
  const [errors, setErrors] = useState<CourseBundleErrorsInterface>(defaultErrors)

  const { courseBundles, mutate, counter } = useCourseBundles({ limit, pagination })

  const { revalidateModules } = useRevalidateModules()

  const { t } = useLocoTranslation()

  const columns: ColumnDefinitionInterface<CourseBundleItemInterface>[] = [
    {
      key: 'name',
      header: t('global.title'),
      widthPercent: 30,
      onRender: courseBundle => (
        <div className="truncate">
          <LinkWithoutPrefetch
            rel="noreferrer"
            className="text-brand-primary hover:text-text-secondary ring-brand-primary rounded-xs w-full outline-hidden ring-offset-1 focus-visible:ring-2"
            href={`/courses/bundles/${courseBundle.id}`}
          >
            {courseBundle.name}
          </LinkWithoutPrefetch>
        </div>
      ),
    },
    {
      key: 'cover',
      header: t('dashboard.course_bundle.cover_title'),
      widthPercent: 30,
      onRender: ({ image }) =>
        image && (
          <img
            className={'h-[108px] w-[192px] object-contain'}
            alt={image.filename}
            src={image.path}
          />
        ),
    },
    {
      key: 'description',
      header: t('global.description'),
      widthPercent: 40,
    },
    {
      key: 'instructorName',
      header: t('dashboard.course.instructor_name_title'),
      widthPercent: 20,
    },
    {
      key: 'active',
      header: t('global.status'),
      widthPercent: 15,
      onRender: courseBundle => (
        <div className="flex items-center lg:ml-4">
          <Tooltip
            label={courseBundle.deactivatedAt ? t('global.inactive') : t('global.active')}
            mode="hover"
            key="status-tooltip"
          >
            <CheckCircleIcon
              className={`h-[16px] w-[16px] sm:h-[20px] sm:w-[20px] ${
                !courseBundle.deactivatedAt && 'fill-green'
              }`}
            />
          </Tooltip>
        </div>
      ),
    },
  ]

  const onActivateCourseBundle = async (courseBundle: CourseBundleItemInterface) => {
    const update = courseBundle.deactivatedAt ? 'activate' : 'deactivate'
    await activateCourseBundle(update, courseBundle.id)
    await mutate(data => {
      if (data) {
        return {
          items: data.items.map(item => {
            if (item.id === courseBundle.id)
              item = { ...item, deactivatedAt: item.deactivatedAt ? null : 'deactivated' }
            return item
          }),
          hasMore: data.hasMore,
        }
      }
    }, false)
    await revalidateModules([RequestModuleEnum.CourseBundle])
  }

  const onRemoveCourseBundle = async (courseBundle: CourseBundleItemInterface) => {
    await removeCourseBundle(courseBundle.id)
    await revalidateModules([RequestModuleEnum.CourseBundle], true)
  }

  const onCreateCourseBundle = async (data: CreateDataType<CourseBundleCreateInterface>) => {
    await fetchWithErrors(async () => {
      await createCourseBundle({
        name: data.name,
        description: data.description,
        instructorName: data.instructorName,
        image: data.image?.id || null,
      })
      await revalidateModules([RequestModuleEnum.CourseBundle], true)
    }, setErrors)
  }

  const onEditCourseBundle = async (
    courseBundle: CourseBundleItemInterface,
    data: CourseBundleItemInterface,
  ) => {
    await fetchWithErrors(async () => {
      const response = await updateCourseBundle(courseBundle.id, {
        name: data.name,
        description: data.description,
        instructorName: data.instructorName,
        image: data.image?.id || null,
      })
      await mutate(data => {
        if (data) {
          return {
            items: data.items.map(item => {
              if (item.id === courseBundle.id) item = response.data
              return item
            }),
            hasMore: data.hasMore,
          }
        }
      }, false)
      await revalidateModules([RequestModuleEnum.CourseBundle])
    }, setErrors)
  }

  const onModalClose = () => setErrors(defaultErrors)

  return (
    <>
      <TabMenu showAssigments={flags[GrowthbookFlagsEnum.Assignment]} />
      <ListingLayout
        data={courseBundles}
        counter={counter}
        title={t('dashboard.bundles.title', {
          count: counter?.count || 1,
        })}
        createData={{
          caption: t('dashboard.course_bundle.title_one'),
          onAfterLeave: onModalClose,
          defaultValues: defaultValues,
          requiredFields: ['name'],
          onCreate: data => onCreateCourseBundle(data),
          creatable: [
            {
              field: 'name',
              onRender: (value, onChange) => (
                <FormInput
                  key="course-bundle-name-create"
                  value={value}
                  label={t('global.name')}
                  onChange={e => {
                    onChange(e.target.value)
                  }}
                  maxLength={255}
                  error={errors.name}
                />
              ),
            },
            {
              field: 'description',
              onRender: (value, onChange) => (
                <FormInput
                  key="course-bundle-description-create"
                  value={value || ''}
                  onChange={e => onChange(e.target.value)}
                  error={errors.name}
                  label={t('global.description')}
                />
              ),
            },
            {
              field: 'instructorName',
              onRender: (value, onChange) => (
                <FormInput
                  key="course-bundle-name-create"
                  value={value || ''}
                  onChange={e => onChange(e.target.value)}
                  error={errors.name}
                  label={t('dashboard.course.instructor_name_title')}
                />
              ),
            },
            {
              field: 'image',
              onRender: (value, onChange) => (
                <ImageUploaderWithPreviewImage
                  key="course-bundle-image-create"
                  label={
                    <Hint label={t('dashboard.course.image_popover_title')}>
                      {t('dashboard.course_bundle.cover_title')}
                    </Hint>
                  }
                  previewClassName="h-[200px] w-[364px]"
                  value={value}
                  onRemove={() => {
                    onChange(null)
                  }}
                  onSelect={onChange}
                  aspectRatio={16 / 9}
                  source={FileSourceEnum.Membership}
                />
              ),
            },
          ],
        }}
        type={'compact'}
      >
        <Table
          caption={t('dashboard.course_bundle.title_one')}
          data={courseBundles}
          columns={columns}
          limiter={{ limit, setLimit }}
          paginator={{ pagination, setPagination }}
          actions={{
            openLink: [
              {
                name: 'view-course-bundle',
                openInNew: false,
                setHref: courseBundle => `/courses/bundles/${courseBundle.id}`,
                isNext: true,
              },
            ],
            settings: {
              onModalClose: onModalClose,
              onEdit: (courseBundle, data) => onEditCourseBundle(courseBundle, data),
              editable: [
                {
                  field: 'name',
                  onRender: (value, onChange) => (
                    <FormInput
                      key="course-bundle-name-edit"
                      value={value}
                      onChange={e => onChange(e.target.value)}
                      error={errors.name}
                      label={t('global.name')}
                    />
                  ),
                },
                {
                  field: 'description',
                  onRender: (value, onChange) => (
                    <FormInput
                      key="course-bundle-description-edit"
                      value={value || ''}
                      onChange={e => onChange(e.target.value)}
                      error={errors.name}
                      label={t('global.description')}
                    />
                  ),
                },
                {
                  field: 'instructorName',
                  onRender: (value, onChange) => (
                    <FormInput
                      key="course-bundle-name-edit"
                      value={value || ''}
                      onChange={e => onChange(e.target.value)}
                      error={errors.name}
                      label={t('dashboard.course.instructor_name_title')}
                    />
                  ),
                },
                {
                  field: 'image',
                  onRender: (value, onChange) => (
                    <ImageUploaderWithPreviewImage
                      key="course-bundle-image-edit"
                      label={
                        <Hint label={t('dashboard.course.image_popover_title')}>
                          {t('dashboard.course_bundle.cover_title')}
                        </Hint>
                      }
                      value={value}
                      onRemove={() => {
                        onChange(null)
                      }}
                      onSelect={onChange}
                      aspectRatio={16 / 9}
                      source={FileSourceEnum.Membership}
                      previewClassName="h-[200px] w-[364px]"
                    />
                  ),
                },
              ],
            },
            custom: [
              {
                name: 'activate-course-bundle',
                shouldRender: courseBundle => !!courseBundle.deactivatedAt,
                caption: t('dashboard.actions.title.activate'),
                onActionRender: (opened, onClose, courseBundle, key) => (
                  <Confirmation
                    key={key}
                    opened={opened}
                    onClose={onClose}
                    onConfirm={() => onActivateCourseBundle(courseBundle)}
                    confirmationText={t('dashboard.actions.activate_confirmation', {
                      activate: t('global.activate').toLowerCase(),
                      module: t('dashboard.course_bundle.title_one').toLowerCase(),
                    })}
                    toastCaption={t('dashboard.actions.activated', {
                      module: t('dashboard.course_bundle.title_one'),
                      activated: t('dashboard.actions.activated_caption').toLowerCase(),
                    })}
                  />
                ),
              },
              {
                name: 'deactivate-course-bundle',
                shouldRender: courseBundle => !courseBundle.deactivatedAt,
                caption: t('dashboard.actions.title.deactivate'),
                onActionRender: (opened, onClose, courseBundle, key) => (
                  <Confirmation
                    key={key}
                    opened={opened}
                    onClose={onClose}
                    onConfirm={() => onActivateCourseBundle(courseBundle)}
                    confirmationText={t('dashboard.actions.activate_confirmation', {
                      activate: t('global.deactivate').toLowerCase().toLowerCase(),
                      module: t('dashboard.course_bundle.title_one').toLowerCase(),
                    })}
                    toastCaption={t('dashboard.actions.activated', {
                      module: t('dashboard.course_bundle.title_one'),
                      activated: t('dashboard.actions.deactivated_caption').toLowerCase(),
                    })}
                  />
                ),
              },
            ],
            remove: {
              onChange: courseBundle => onRemoveCourseBundle(courseBundle),
            },
          }}
        />
      </ListingLayout>
    </>
  )
}

export const getServerSideProps = getCombinedServerSideProps(
  getBaseServerSideProps(CourseBundlesLoco, 'header.top_menu.course_bundles'),
  getServerSideFlagsProps(),
)

export default CourseBundles
